import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_TONE,
  MODE_LABEL,
  PAYMENT_HINT,
  PAYMENT_LABEL,
  day,
  instant,
  mad,
} from '../lib/format';
import { Badge, CopyValue, Field, Notice, Sheet, Spinner } from '../components/ui';
import type { Booking, PaymentMethod } from '../lib/types';
import { t } from '../lib/i18n';
import { translateServerText } from '../lib/server-text';

/**
 * One booking, for whichever side is looking at it.
 *
 * The same page serves guest and host because the actions available are derived
 * from the booking's status and who you are, not from two separate screens that
 * would drift apart.
 */

type Detail = {
  booking: Booking;
  events: { id: string; fromStatus: string | null; toStatus: string; reason: string | null; createdAt: string }[];
};

export default function BookingDetail() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const { user, toast, reportError } = useApp();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>('CARD_CMI');
  const [instructions, setInstructions] = useState<{ label: string; value: string }[] | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundPreview, setRefundPreview] = useState<{
    decision: { refundCentimes: number; tierLabel: string; hoursBeforeStart: number };
    capturedCentimes: number;
  } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewBody, setReviewBody] = useState('');
  const [reviewWindow, setReviewWindow] = useState<{ canReview: boolean; alreadyReviewed: boolean } | null>(
    null,
  );
  const [address, setAddress] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const response = await api.get<Detail>(`/bookings/${id}`);
      setDetail(response);
      if (response.booking.status === 'COMPLETED') {
        const window = await api
          .get<{ canReview: boolean; alreadyReviewed: boolean }>(`/bookings/${id}/review-window`)
          .catch(() => null);
        setReviewWindow(window);
      }
      if (['CONFIRMED', 'COMPLETED'].includes(response.booking.status)) {
        const addressResponse = await api
          .get<{ addressPrecise: string | null }>(`/properties/${response.booking.property.id}/address`)
          .catch(() => null);
        setAddress(addressResponse?.addressPrecise ?? null);
      }
    } catch (error) {
      reportError(error, t("Réservation introuvable"));
    } finally {
      setLoading(false);
    }
  }, [id, reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const openCancel = async () => {
    setCancelOpen(true);
    try {
      const preview = await api.get<typeof refundPreview>(`/bookings/${id}/cancellation-preview`);
      setRefundPreview(preview);
    } catch {
      setRefundPreview(null);
    }
  };

  if (loading) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="page">
        <Notice tone="danger">{t("Cette réservation est introuvable.")}</Notice>
      </div>
    );
  }

  const { booking, events } = detail;
  const isGuest = user?.id === booking.guest.id;
  const isHost = user?.id === booking.host.id;
  const counterpart = isGuest ? booking.host : booking.guest;
  const breakdown = booking.breakdown;

  /*
   * Where the card gateway drops the payer. The value is only a hint for the
   * message: the booking status above it comes from the server, which learned it
   * from a signed callback, so an edited query string changes the wording and
   * nothing else.
   */
  const paymentOutcome = search.get('paiement');

  return (
    <div className="page page--mid stack stack--loose">
      {paymentOutcome === 'ok' ? (
        <Notice tone="ok">
          {t("Paiement reçu. Les frais Bedly sont réglés — le loyer se paie en main propre au rendez-vous.")}
        </Notice>
      ) : null}
      {paymentOutcome === 'refuse' ? (
        <Notice tone="danger">
          {t("Paiement refusé par la banque. Rien n’a été débité : vous pouvez réessayer ci-dessous.")}
        </Notice>
      ) : null}
      {paymentOutcome === 'deja-regle' ? (
        <Notice tone="info">{t("Ces frais ont déjà été réglés.")}</Notice>
      ) : null}
      {paymentOutcome === 'erreur' ? (
        <Notice tone="warn">
          {t("Nous n’avons pas pu lire la réponse de la banque. Si le montant a été débité, il sera rapproché automatiquement — sinon réessayez.")}
        </Notice>
      ) : null}
      <div className="stack stack--tight">
        <Link to="/compte" className="small">
          {t("← Mes réservations")}
        </Link>
        <div className="row row--between row--wrap" style={{ gap: 10 }}>
          <h1 style={{ fontSize: 24 }}>{booking.property.title}</h1>
          <Badge tone={BOOKING_STATUS_TONE[booking.status]}>{BOOKING_STATUS_LABEL[booking.status]}</Badge>
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          {MODE_LABEL[booking.mode]} · {booking.property.neighborhood}, {t(booking.property.city)}
          {booking.room ? ` · ${booking.room.name}` : ''}
        </p>
      </div>

      <div className="card card--pad stack">
        <div className="row row--wrap" style={{ gap: 22 }}>
          <div className="stack stack--tight">
            <span className="field__label">{t("Référence")}</span>
            <span className="strong">{booking.reference}</span>
          </div>
          <div className="stack stack--tight">
            <span className="field__label">{t("Arrivée")}</span>
            <span className="strong">{day(booking.startDate)}</span>
          </div>
          <div className="stack stack--tight">
            <span className="field__label">{t("Départ")}</span>
            <span className="strong">{day(booking.endDate)}</span>
          </div>
          <div className="stack stack--tight">
            <span className="field__label">{t("Voyageurs")}</span>
            <span className="strong">{booking.guests}</span>
          </div>
        </div>

        {booking.guestMessage ? (
          <div className="stack stack--tight">
            <span className="field__label">{t("Message du locataire")}</span>
            <p className="small" style={{ margin: 0 }}>
              {booking.guestMessage}
            </p>
          </div>
        ) : null}

        {address ? (
          <Notice tone="ok">
            {t("Adresse exacte :")} <strong>{address}</strong>
          </Notice>
        ) : isGuest && booking.status !== 'CONFIRMED' && booking.status !== 'COMPLETED' ? (
          <Notice tone="info">
            {t("L’adresse exacte se débloque dès que les frais de service sont réglés.")}
          </Notice>
        ) : null}
      </div>

      {breakdown ? (
        <div className="card card--pad stack stack--tight">
          <h3>{t("Détail du prix")}</h3>
          <div className="price-line">
            <span>
              {mad(breakdown.hostPriceCentimes)}
              <span className="muted">
                {' '}
                {t("×")} {breakdown.unit === 'night' ? t('{count} nuit(s)', { count: breakdown.periods }) : t('{count} mois', { count: breakdown.periods })}
              </span>
            </span>
            <span>{mad(breakdown.hostTotalCentimes)}</span>
          </div>
          <div className="price-line">
            <span>
              {t("Frais de service Bedly")}
              <span className="price-line__note"> {t("· payés en ligne, une seule fois")}</span>
            </span>
            <span>{mad(breakdown.platformFeeCentimes)}</span>
          </div>
          <div className="price-line price-line--total">
            <span>{t("Total du séjour")}</span>
            <span>{mad(breakdown.totalCentimes)}</span>
          </div>
          <div className="price-line">
            <span className="muted small">{t("Payé en ligne")}</span>
            <span className="small">{mad(breakdown.dueNowCentimes)}</span>
          </div>
          <div className="price-line">
            <span className="muted small">{t("À remettre en espèces à l’hôte")}</span>
            <span className="small">{mad(breakdown.dueInCashCentimes)}</span>
          </div>
          {booking.amounts.refundCentimes !== null ? (
            <div className="price-line">
              <span className="muted">{t("Remboursement des frais")}</span>
              <span className="strong">{mad(booking.amounts.refundCentimes)}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Host actions */}
      {isHost && booking.status === 'PENDING' ? (
        <div className="card card--pad stack">
          <h3>{t("Répondre à la demande")}</h3>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Les dates sont bloquées jusqu’à votre réponse")}
            {booking.expiresAt ? ` ${t('(avant le {date})', { date: instant(booking.expiresAt) })}` : ''}.
          </p>
          <div className="row" style={{ gap: 10 }}>
            <button
              className="btn"
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await api.post(`/bookings/${booking.id}/accept`);
                  toast(t("Demande acceptée. Le locataire peut régler les frais de service."), 'ok');
                })
              }
            >
              {t("Accepter")}
            </button>
            <button
              className="btn btn--danger"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await api.post(`/bookings/${booking.id}/decline`, {
                    reason: 'Indisponible à ces dates',
                  });
                  toast(t("Demande refusée. Les dates sont libérées."));
                })
              }
            >
              {t("Refuser")}
            </button>
          </div>
        </div>
      ) : null}

      {/* Guest payment */}
      {isGuest && (booking.status === 'ACCEPTED' || booking.status === 'AWAITING_PAYMENT') ? (
        <div className="card card--pad stack">
          <h3>{t("Régler les frais de service")}</h3>
          <Notice tone="info">
            {t("Bedly n’encaisse que ses frais de service (")}{mad(booking.amounts.platformFeeCentimes)}{t("). Le loyer de")} {mad(booking.amounts.hostTotalCentimes)} {t("se règle en espèces directement à l’hôte lors de la remise des clés.")}
          </Notice>
          <div className="stack stack--tight">
            {(Object.keys(PAYMENT_LABEL) as PaymentMethod[]).map((option) => (
              <button
                key={option}
                type="button"
                className={`chip ${method === option ? 'chip--on' : ''}`}
                style={{ justifyContent: 'flex-start', width: '100%', padding: '12px 14px' }}
                onClick={() => setMethod(option)}
              >
                <span className="stack stack--tight" style={{ alignItems: 'flex-start' }}>
                  <span className="strong">{PAYMENT_LABEL[option]}</span>
                  <span className="tiny muted">{PAYMENT_HINT[option]}</span>
                </span>
              </button>
            ))}
          </div>
          <button
            className="btn btn--block"
            disabled={busy}
            onClick={() =>
              act(async () => {
                const response = await api.post<{
                  checkoutUrl: string | null;
                  instructions: { label: string; value: string }[] | null;
                }>(`/bookings/${booking.id}/payments`, { method });
                if (response.checkoutUrl) {
                  window.location.href = response.checkoutUrl;
                  return;
                }
                setInstructions(response.instructions ?? []);
              })
            }
          >
            {busy ? t("Préparation…") : t("Payer les frais de service")}
          </button>
          {instructions ? (
            <div className="card card--pad stack stack--tight">
              <h3>{t("Instructions")}</h3>
              {instructions.map((line) => (
                <CopyValue key={line.label} label={translateServerText(line.label)} value={line.value} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Review */}
      {booking.status === 'COMPLETED' && reviewWindow?.canReview ? (
        <div className="card card--pad stack">
          <h3>{t("Laisser un avis")}</h3>
          <p className="small muted" style={{ margin: 0 }}>
            {t("Votre avis reste masqué jusqu’à ce que l’autre partie réponde, ou 14 jours après la fin du séjour. Personne ne peut lire le vôtre avant d’écrire le sien.")}
          </p>
          <button className="btn" onClick={() => setReviewOpen(true)}>
            {t("Écrire mon avis")}
          </button>
        </div>
      ) : null}
      {reviewWindow?.alreadyReviewed ? (
        <Notice tone="ok">{t("Votre avis est enregistré.")}</Notice>
      ) : null}

      {/* Cancellation */}
      {['PENDING', 'ACCEPTED', 'AWAITING_PAYMENT', 'CONFIRMED'].includes(booking.status) ? (
        <div className="card card--pad row row--between row--wrap" style={{ gap: 12 }}>
          <div className="stack stack--tight">
            <span className="strong">{t("Annuler la réservation")}</span>
            <span className="small muted">
              {t("Le remboursement porte sur les frais de service. Plus de 7 jours avant : intégral. Entre 7 jours et 48 h : 50 %. Moins de 48 h : non remboursés.")}
            </span>
          </div>
          <button className="btn btn--danger" onClick={openCancel}>
            {t("Annuler")}
          </button>
        </div>
      ) : null}

      <div className="card card--pad stack stack--tight">
        <div className="row row--between">
          <h3>{t("Interlocuteur")}</h3>
          <Link className="btn btn--ghost btn--sm" to="/messages">
            {t("Ouvrir la messagerie")}
          </Link>
        </div>
        <span className="small">
          {isGuest ? t("Hôte") : t("Locataire")} : <span className="strong">{counterpart.fullName}</span>
        </span>
      </div>

      <div className="card card--pad stack stack--tight">
        <h3>{t("Historique")}</h3>
        {events.map((event) => (
          <div key={event.id} className="price-line">
            <span className="small">
              {BOOKING_STATUS_LABEL[event.toStatus] ?? event.toStatus}
              {event.reason ? <span className="muted"> — {event.reason}</span> : null}
            </span>
            <span className="tiny muted nowrap">{instant(event.createdAt)}</span>
          </div>
        ))}
      </div>

      {cancelOpen ? (
        <Sheet title={t("Annuler la réservation")} onClose={() => setCancelOpen(false)}>
          <div className="stack">
            {refundPreview ? (
              <Notice tone={refundPreview.decision.refundCentimes > 0 ? 'info' : 'warn'}>
                {refundPreview.capturedCentimes === 0
                  ? t("Aucun frais n’a encore été encaissé : rien à rembourser.")
                  : t('Remboursement estimé des frais : {amount} ({tier}).', {
                      amount: mad(refundPreview.decision.refundCentimes),
                      tier: translateServerText(refundPreview.decision.tierLabel),
                    })}
              </Notice>
            ) : (
              <Spinner label={t("Calcul du remboursement…")} />
            )}
            <Field label={t("Motif (optionnel)")}>
              <textarea
                className="textarea"
                rows={3}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
            </Field>
            <button
              className="btn btn--danger btn--block"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await api.post(`/bookings/${booking.id}/cancel`, {
                    ...(cancelReason ? { reason: cancelReason } : {}),
                  });
                  setCancelOpen(false);
                  toast(t("Réservation annulée."));
                })
              }
            >
              {t("Confirmer l’annulation")}
            </button>
          </div>
        </Sheet>
      ) : null}

      {reviewOpen ? (
        <Sheet title={t("Votre avis")} onClose={() => setReviewOpen(false)}>
          <div className="stack">
            <Field label={t("Note globale")}>
              <div className="row" style={{ gap: 6 }}>
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`chip ${rating >= value ? 'chip--on' : ''}`}
                    onClick={() => setRating(value)}
                    aria-label={t('{count} étoile(s)', { count: value })}
                  >
                    ★
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t("Votre commentaire")}>
              <textarea
                className="textarea"
                rows={4}
                value={reviewBody}
                onChange={(event) => setReviewBody(event.target.value)}
                placeholder={t("Le logement correspondait-il à l’annonce ? L’hôte était-il réactif ?")}
              />
            </Field>
            <button
              className="btn btn--block"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const response = await api.post<{ message: string }>(
                    `/bookings/${booking.id}/reviews`,
                    { rating, ...(reviewBody ? { body: reviewBody } : {}) },
                  );
                  setReviewOpen(false);
                  toast(translateServerText(response.message), 'ok');
                })
              }
            >
              {t("Envoyer mon avis")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
